import {
  Box as Flex,
  Typography as Text,
  makeStyles,
  styled,
} from '@material-ui/core';

import classNames from 'classnames';
import sum from 'lodash/sum';

import { useColors } from '@spotify/confidence-charts-react';
import { extractLastNameComponent } from '@spotify/confidence-core-react';
import {
  ActivityItem,
  DeltaText,
} from '@spotify/plugin-confidence-activity-react';

import { type ChangeStatus } from '../NormalizedExpressionDelta';
import type { Rule } from './types';

export const round = (num: number, decimalPlaces = 3) => {
  const p = Math.pow(10, decimalPlaces || 0);
  const n = num * p * (1 + Number.EPSILON);
  return Math.round(n) / p;
};

const extractAssignments = (rule: Rule) => {
  const bucketCount = rule.assignmentSpec?.bucketCount || 0;

  return (
    rule.assignmentSpec?.assignments.map((assignment, index) => {
      return {
        key: assignment.assignmentId || `assignment-${index}`,
        name:
          assignment.variant?.variant ||
          assignment.assignmentId ||
          `assignment-${index}`,
        variant: extractLastNameComponent(assignment.variant?.variant),
        fallthrough:
          assignment.fallthrough !== undefined &&
          assignment.fallthrough !== null,
        clientDefault:
          assignment.clientDefault !== undefined &&
          assignment.clientDefault !== null,
        percentage:
          ((sum(assignment.bucketRanges.map(br => br.upper)) -
            sum(assignment.bucketRanges.map(br => br.lower))) /
            bucketCount) *
          100,
      };
    }) ?? []
  );
};

const useStyles = makeStyles({
  container: {
    background: 'var(--background-base)',
    display: 'inline-flex',
    padding: 'var(--encore-spacing-tighter-4) var(--encore-spacing-tighter-2)',
    borderRadius: 'var(--encore-corner-radius-base)',
  },
  addedContainer: {
    border: '1px solid var(--essential-positive)',
  },
  removedContainer: {
    border: '1px solid var(--text-subdued)',
    opacity: 0.6,
  },
  modifiedContainer: {},
});

const FlexContainer = styled(Flex)({});

const Dot = styled('span')(({ theme }) => ({
  display: 'inline-block',
  height: theme.typography.caption.fontSize,
  width: theme.typography.caption.fontSize,
  borderRadius: '50%',
  alignSelf: 'center',
}));

const VariantChip = ({
  name,
  variant,
  fallthrough,
  clientDefault,
  oldPercentage,
  newPercentage,
  change,
}: {
  name: string;
  variant?: string;
  fallthrough: boolean;
  clientDefault: boolean;
  oldPercentage?: number;
  newPercentage?: number;
  change?: ChangeStatus;
}) => {
  const classes = useStyles();
  const { getChartColorForKey } = useColors();

  const oldPercentageText =
    oldPercentage !== undefined ? `${round(oldPercentage)}%` : '';
  const newPercentageText =
    newPercentage !== undefined ? `${round(newPercentage)}%` : '';

  return (
    <FlexContainer
      display="flex"
      alignItems="center"
      style={{ gap: 'var(--encore-spacing-tighter-4)' }}
      className={classNames(
        classes.container,
        change === 'added' && classes.addedContainer,
        change === 'removed' && classes.removedContainer,
        change === 'modified' && classes.modifiedContainer,
      )}
    >
      <Dot
        style={{
          backgroundColor: getChartColorForKey(name)!,
        }}
      />
      <div style={{ minWidth: '4ch' }}>
        {oldPercentageText &&
        newPercentageText &&
        oldPercentageText !== newPercentageText ? (
          <DeltaText
            oldText={oldPercentageText}
            newText={newPercentageText}
            disableDiff
          />
        ) : (
          <Text variant="body1" style={{ minWidth: '4ch' }}>
            {newPercentageText}
          </Text>
        )}
      </div>
      {variant && <Text variant="body1">{variant}</Text>}
      {fallthrough && (
        <Text variant="body1" color="textSecondary">
          fallthrough
        </Text>
      )}
      {clientDefault && (
        <Text variant="body1" color="textSecondary">
          client default
        </Text>
      )}
    </FlexContainer>
  );
};

export const ServeSection = ({
  oldRule,
  newRule,
}: {
  oldRule?: Rule;
  newRule?: Rule;
}) => {
  // Ignore rule prop - only use oldRule and newRule
  const oldAssignments = oldRule ? extractAssignments(oldRule) : [];
  const newAssignments = newRule ? extractAssignments(newRule) : [];

  let assignmentsWithChange: Array<{
    key: string;
    name: string;
    variant?: string;
    fallthrough: boolean;
    clientDefault: boolean;
    change?: ChangeStatus;
    oldPercentage?: number;
    newPercentage?: number;
  }> = [];

  if (oldRule && newRule) {
    // Both old and new rules exist - compute diff
    const oldAssignmentMap = new Map(oldAssignments.map(a => [a.key, a]));
    const newAssignmentMap = new Map(newAssignments.map(a => [a.key, a]));

    // Process new assignments
    for (const newAssignment of newAssignments) {
      const oldAssignment = oldAssignmentMap.get(newAssignment.key);

      let change: ChangeStatus;
      let oldPercentage: number | undefined;
      let newPercentage: number | undefined;

      if (!oldAssignment) {
        // Added assignment
        change = 'added';
        newPercentage = newAssignment.percentage;
      } else {
        oldPercentage = oldAssignment.percentage;
        newPercentage = newAssignment.percentage;

        // Assignment exists in both
        if (oldAssignment.percentage !== newAssignment.percentage) {
          change = 'modified';
        } else {
          change = 'unchanged';
        }
      }

      assignmentsWithChange.push({
        ...newAssignment,
        change,
        oldPercentage,
        newPercentage,
      });
    }

    // Include removed assignments (exist in old but not in new)
    for (const oldAssignment of oldAssignments) {
      if (!newAssignmentMap.has(oldAssignment.key)) {
        assignmentsWithChange.push({
          ...oldAssignment,
          change: 'removed' as ChangeStatus,
          oldPercentage: oldAssignment.percentage,
          newPercentage: undefined,
        });
      }
    }
  } else if (newRule) {
    // Only newRule is set - show without change indicators
    assignmentsWithChange = newAssignments.map(assignment => ({
      ...assignment,
      change: undefined,
      newPercentage: assignment.percentage,
    }));
  } else if (oldRule) {
    // Only oldRule is set - show without change indicators
    assignmentsWithChange = oldAssignments.map(assignment => ({
      ...assignment,
      change: undefined,
      newPercentage: assignment.percentage,
    }));
  }

  return (
    <ActivityItem.Section label="Serve">
      <Flex
        display="flex"
        flexDirection="row"
        alignItems="flex-start"
        style={{
          flexWrap: 'wrap',
          gap: 'var(--encore-spacing-tighter-4)',
        }}
      >
        {assignmentsWithChange
          .filter(assignment => {
            // Show if percentage > 0, or if it's a removed assignment
            return (
              (assignment.newPercentage ?? 0) > 0 ||
              assignment.change === 'removed'
            );
          })
          .map(assignment => (
            <VariantChip
              key={assignment.key}
              name={assignment.name}
              variant={assignment.variant}
              oldPercentage={assignment.oldPercentage}
              newPercentage={assignment.newPercentage}
              change={assignment.change}
              fallthrough={assignment.fallthrough}
              clientDefault={assignment.clientDefault}
            />
          ))}
      </Flex>
    </ActivityItem.Section>
  );
};
